import React, {useState} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Alert,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {Colors} from '../theme/colors';
import {register} from '../services/auth';
import {useAuth} from '../hooks/useAuth';
import {Icon} from '../components/Icon';

type RootStackParamList = {
  Home: undefined;
  Login: undefined;
  Register: undefined;
};

type RegisterScreenProps = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Register'>;
};

export const RegisterScreen: React.FC<RegisterScreenProps> = ({navigation}) => {
  const insets = useSafeAreaInsets();
  const {refreshUser} = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Focus states
  const [firstNameFocused, setFirstNameFocused] = useState(false);
  const [lastNameFocused, setLastNameFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmFocused, setConfirmFocused] = useState(false);

  const handleRegister = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('Inserisci nome e cognome');
      return;
    }
    if (!email.trim()) {
      setError('Inserisci la tua email');
      return;
    }
    if (!password.trim() || password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('Le password non coincidono');
      return;
    }

    setIsLoading(true);
    setError('');

    const result = await register(
      firstName.trim(),
      lastName.trim(),
      email.trim(),
      password,
      passwordConfirmation,
    );

    setIsLoading(false);

    if (result.success) {
      if (result.requires_verification) {
        Alert.alert(
          'Registrazione completata',
          'Controlla la tua email per confermare l\'account, poi torna ad accedere.',
          [{text: 'OK', onPress: () => navigation.goBack()}],
        );
      } else {
        // Auto-logged in
        await refreshUser();
        navigation.popToTop();
      }
    } else {
      setError(result.message || 'Errore durante la registrazione');
    }
  };

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          bounces={false}
        >
          {/* Purple Header with Logo */}
          <View style={[styles.purpleHeader, {paddingTop: insets.top + 16}]}>
            <TouchableOpacity
              style={[styles.closeButton, {top: insets.top + 8}]}
              onPress={() => navigation.goBack()}
              hitSlop={{top: 12, bottom: 12, left: 12, right: 12}}
            >
              <Icon name="xmark" size={20} color="#FFFFFF" />
            </TouchableOpacity>
            <Image
              source={{uri: 'https://cdn.laviola.it/laviola/logo-payoff.png'}}
              style={styles.logo}
              resizeMode="contain"
            />
          </View>

          {/* White Form Area */}
          <View style={styles.formContainer}>
            <View style={styles.form}>
              {/* Title */}
              <View style={styles.titleRow}>
                <Icon name="plus" size={22} color={Colors.primary} />
                <Text style={styles.title}>Crea il tuo account</Text>
              </View>

              <Text style={styles.subtitle}>
                Registrati per accedere a tutte le funzionalità dell'app.
              </Text>

              {error ? (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              ) : null}

              {/* First Name */}
              <Text style={styles.inputLabel}>Nome</Text>
              <View style={[
                styles.inputContainer,
                firstNameFocused && styles.inputContainerFocused,
              ]}>
                <Icon
                  name="user"
                  size={16}
                  color={firstNameFocused ? Colors.primary : Colors.textSecondary}
                />
                <TextInput
                  style={styles.input}
                  placeholder=""
                  placeholderTextColor={Colors.textSecondary}
                  value={firstName}
                  onChangeText={setFirstName}
                  autoCapitalize="words"
                  autoCorrect={false}
                  onFocus={() => setFirstNameFocused(true)}
                  onBlur={() => setFirstNameFocused(false)}
                />
              </View>

              {/* Last Name */}
              <Text style={styles.inputLabel}>Cognome</Text>
              <View style={[
                styles.inputContainer,
                lastNameFocused && styles.inputContainerFocused,
              ]}>
                <Icon
                  name="user"
                  size={16}
                  color={lastNameFocused ? Colors.primary : Colors.textSecondary}
                />
                <TextInput
                  style={styles.input}
                  placeholder=""
                  placeholderTextColor={Colors.textSecondary}
                  value={lastName}
                  onChangeText={setLastName}
                  autoCapitalize="words"
                  autoCorrect={false}
                  onFocus={() => setLastNameFocused(true)}
                  onBlur={() => setLastNameFocused(false)}
                />
              </View>

              {/* Email */}
              <Text style={styles.inputLabel}>Email</Text>
              <View style={[
                styles.inputContainer,
                emailFocused && styles.inputContainerFocused,
              ]}>
                <Icon
                  name="envelope"
                  size={16}
                  color={emailFocused ? Colors.primary : Colors.textSecondary}
                />
                <TextInput
                  style={styles.input}
                  placeholder=""
                  placeholderTextColor={Colors.textSecondary}
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                />
              </View>

              {/* Password */}
              <Text style={styles.inputLabel}>Password</Text>
              <View style={[
                styles.inputContainer,
                passwordFocused && styles.inputContainerFocused,
              ]}>
                <Icon
                  name="lock"
                  size={16}
                  color={passwordFocused ? Colors.primary : Colors.textSecondary}
                />
                <TextInput
                  style={styles.input}
                  placeholder=""
                  placeholderTextColor={Colors.textSecondary}
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry={!showPassword}
                  autoCapitalize="none"
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                />
                <TouchableOpacity
                  onPress={() => setShowPassword(!showPassword)}
                  style={styles.eyeButton}
                >
                  <Icon
                    name={showPassword ? 'eye' : 'eye-slash'}
                    size={14}
                    color={Colors.textSecondary}
                  />
                </TouchableOpacity>
              </View>

              {/* Confirm Password */}
              <Text style={styles.inputLabel}>Conferma Password</Text>
              <View style={[
                styles.inputContainer,
                confirmFocused && styles.inputContainerFocused,
              ]}>
                <Icon
                  name="lock"
                  size={16}
                  color={confirmFocused ? Colors.primary : Colors.textSecondary}
                />
                <TextInput
                  style={styles.input}
                  placeholder=""
                  placeholderTextColor={Colors.textSecondary}
                  value={passwordConfirmation}
                  onChangeText={setPasswordConfirmation}
                  secureTextEntry={!showConfirmPassword}
                  autoCapitalize="none"
                  onFocus={() => setConfirmFocused(true)}
                  onBlur={() => setConfirmFocused(false)}
                />
                <TouchableOpacity
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={styles.eyeButton}
                >
                  <Icon
                    name={showConfirmPassword ? 'eye' : 'eye-slash'}
                    size={14}
                    color={Colors.textSecondary}
                  />
                </TouchableOpacity>
              </View>

              {/* Register Button */}
              <TouchableOpacity
                style={[styles.registerButton, isLoading && styles.registerButtonDisabled]}
                onPress={handleRegister}
                disabled={isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <View style={styles.registerButtonContent}>
                    <Text style={styles.registerButtonText}>Registrati</Text>
                    <Icon name="chevron-right" size={14} color="#FFFFFF" />
                  </View>
                )}
              </TouchableOpacity>
            </View>

            {/* Login link */}
            <View style={styles.loginContainer}>
              <Text style={styles.loginText}>Hai gia un account? </Text>
              <TouchableOpacity onPress={() => navigation.goBack()}>
                <Text style={styles.loginLink}>Accedi</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.primary,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  purpleHeader: {
    backgroundColor: Colors.primary,
    alignItems: 'center',
    paddingBottom: 30,
    paddingHorizontal: 20,
  },
  closeButton: {
    position: 'absolute',
    right: 16,
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.25)',
    borderRadius: 22,
    zIndex: 10,
  },
  logo: {
    width: 200,
    height: 60,
    marginTop: 0,
  },
  formContainer: {
    flex: 1,
    backgroundColor: Colors.background,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    marginTop: -20,
    paddingTop: 24,
    paddingHorizontal: 20,
    paddingBottom: 30,
  },
  form: {
    backgroundColor: Colors.surface,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.1,
    shadowRadius: 16,
    elevation: 6,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: Colors.textPrimary,
  },
  subtitle: {
    fontSize: 13,
    color: Colors.textSecondary,
    lineHeight: 18,
    marginBottom: 16,
  },
  errorContainer: {
    backgroundColor: '#FFEBEE',
    padding: 12,
    borderRadius: 8,
    marginBottom: 14,
  },
  errorText: {
    color: Colors.error,
    fontSize: 13,
    textAlign: 'center',
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.textPrimary,
    marginBottom: 6,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.surfaceVariant,
    borderRadius: 10,
    paddingHorizontal: 14,
    marginBottom: 12,
    gap: 10,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  inputContainerFocused: {
    borderColor: Colors.primary,
    backgroundColor: Colors.surface,
  },
  input: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 16,
    color: Colors.textPrimary,
  },
  eyeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  registerButton: {
    backgroundColor: Colors.primary,
    paddingVertical: 16,
    borderRadius: 28,
    alignItems: 'center',
    marginTop: 6,
  },
  registerButtonDisabled: {
    opacity: 0.7,
  },
  registerButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  registerButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  loginContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 20,
  },
  loginText: {
    fontSize: 14,
    color: Colors.textSecondary,
  },
  loginLink: {
    fontSize: 14,
    color: Colors.primary,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});
